import Phaser from 'phaser';
import { SCENES, POKEMON_DEMO_DATA } from '../utils/constants';

export class PokemonDemoScene extends Phaser.Scene {
  private selectedPokemon: string | null = null;
  private infoPanel!: Phaser.GameObjects.Container;
  private pokemonSprites: Map<string, Phaser.GameObjects.Image> = new Map();

  constructor() {
    super({ key: SCENES.POKEMON_DEMO });
  }

  create() {
    const { width, height } = this.cameras.main;

    this.cameras.main.setBackgroundColor('#87CEEB');

    this.add
      .text(width / 2, 40, 'Pokemon Sprite Demo', {
        fontSize: '36px',
        color: '#ffffff',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, 85, 'Click on a Pokemon to see more info', {
        fontSize: '16px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 2,
      })
      .setOrigin(0.5);

    this.displayPokemonGrid();
    this.createInfoPanel();

    const backButton = this.add
      .text(width / 2, height - 30, '← Back to Menu', {
        fontSize: '18px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 2,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    backButton.on('pointerover', () => backButton.setScale(1.1));
    backButton.on('pointerout', () => backButton.setScale(1));
    backButton.on('pointerdown', () => this.scene.start(SCENES.MAIN_MENU));
  }

  private displayPokemonGrid() {
    const { width } = this.cameras.main;
    const startY = 160;
    const spacing = 140;
    const pokemonCount = POKEMON_DEMO_DATA.length;
    const totalWidth = pokemonCount * spacing - spacing / 2;
    const startX = (width - totalWidth) / 2 + spacing / 2;

    POKEMON_DEMO_DATA.forEach((pokemon, index) => {
      const x = startX + index * spacing;
      const y = startY;
      const spriteKey = pokemon.name.toLowerCase();

      const sprite = this.add
        .image(x, y, spriteKey)
        .setScale(2)
        .setInteractive({ useHandCursor: true });

      this.pokemonSprites.set(pokemon.name, sprite);

      this.add
        .text(x, y + 70, pokemon.name, {
          fontSize: '18px',
          color: '#ffffff',
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 3,
        })
        .setOrigin(0.5);

      this.add
        .text(x, y + 95, pokemon.types.join(' / '), {
          fontSize: '14px',
          color: '#ffdd88',
          stroke: '#000000',
          strokeThickness: 2,
        })
        .setOrigin(0.5);

      sprite.on('pointerover', () => {
        sprite.setScale(2.2);
        sprite.setTint(0xffff88);
      });

      sprite.on('pointerout', () => {
        sprite.setScale(2);
        sprite.clearTint();
      });

      sprite.on('pointerdown', () => {
        this.showPokemonInfo(pokemon.name, pokemon.types, pokemon.id);
      });
    });
  }

  private createInfoPanel() {
    const { width, height } = this.cameras.main;

    const panelBg = this.add
      .rectangle(0, 0, 400, 200, 0x333333, 0.95)
      .setStrokeStyle(4, 0xffdd00);

    const titleText = this.add
      .text(0, -70, '', {
        fontSize: '28px',
        color: '#ffdd00',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const typeText = this.add
      .text(0, -35, '', {
        fontSize: '18px',
        color: '#ffffff',
      })
      .setOrigin(0.5);

    const idText = this.add
      .text(0, 0, '', {
        fontSize: '16px',
        color: '#aaaaaa',
      })
      .setOrigin(0.5);

    const sourceText = this.add
      .text(0, 30, 'Source: PokeAPI/sprites', {
        fontSize: '14px',
        color: '#88ff88',
      })
      .setOrigin(0.5);

    const docsText = this.add
      .text(0, 55, 'See docs/resources.md for more sprite sources', {
        fontSize: '12px',
        color: '#cccccc',
      })
      .setOrigin(0.5);

    const closeButton = this.add
      .text(0, 85, 'Close', {
        fontSize: '16px',
        color: '#ff8888',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    closeButton.on('pointerover', () => closeButton.setScale(1.1));
    closeButton.on('pointerout', () => closeButton.setScale(1));
    closeButton.on('pointerdown', () => this.hideInfoPanel());

    this.infoPanel = this.add.container(width / 2, height / 2 + 100, [
      panelBg,
      titleText,
      typeText,
      idText,
      sourceText,
      docsText,
      closeButton,
    ]);

    this.infoPanel.setVisible(false);
  }

  private showPokemonInfo(name: string, types: string[], id: number) {
    this.selectedPokemon = name;

    const titleText = this.infoPanel.getAt(1) as Phaser.GameObjects.Text;
    const typeText = this.infoPanel.getAt(2) as Phaser.GameObjects.Text;
    const idText = this.infoPanel.getAt(3) as Phaser.GameObjects.Text;

    titleText.setText(name);
    typeText.setText(`Type: ${types.join(' / ')}`);
    idText.setText(`National Dex #${id}`);

    this.infoPanel.setVisible(true);

    this.tweens.add({
      targets: this.infoPanel,
      alpha: { from: 0, to: 1 },
      scale: { from: 0.8, to: 1 },
      duration: 200,
      ease: 'Back.easeOut',
    });
  }

  private hideInfoPanel() {
    this.tweens.add({
      targets: this.infoPanel,
      alpha: 0,
      scale: 0.8,
      duration: 150,
      ease: 'Back.easeIn',
      onComplete: () => {
        this.infoPanel.setVisible(false);
        this.selectedPokemon = null;
      },
    });
  }
}
